import {
  PropositionLimitSchema,
  PropositionLimit,
  CadendarConsts,
  PatientFlags,
  Transforms,
  Condition,
  RdvTypeValues,
  RdvTypeEnums,
} from "cadendar-shared";
import { useEffect, useReducer } from "react";
import Select2 from "../../components/Select2";
import IconButton from "../main/components/IconButton";
import { FaMinus, FaPlus, FaArrowUp, FaArrowDown } from "react-icons/fa";
import IntegerInput from "../../components/IntegerInput";
import HMInput from "../../components/HMInput";
import { formaters } from "vincent-utils";
import { trpc } from "../main/components/trpc.ts";
import _ from "underts";
import Select4 from "../../components/Select4";

type PropositionLimitState =
  | PropositionLimit
  | {
      type: "shortcutLimit";
      conditionsAND: Condition[];
    };

interface ResetAction {
  type: "reset";
}

interface ChangeMaxPropositionsAction {
  type: "changeMaxPropositions";
  value: number;
}

interface ChangeDaysAction {
  type: "changeDays";
  value: number;
}

interface ChangeTypeAction {
  type: "changeType";
  limitType: "calendarDayLimit" | "opendayLimit" | "calendarWeekLimit";
}

interface ChangeFirstSelectAction {
  type: "changeFirstSelect";
  value: "limit" | "none" | "shortcut";
}

interface AddConditionAction {
  type: "addCondition";
}

interface RemoveConditionAction {
  type: "removeCondition";
  index: number;
}

interface SetConditionTypeAtIndexAction {
  type: "setConditionTypeAtIndex";
  index: number;
  conditionType: string;
}

interface SetConditionAgeValueAtIndexAction {
  type: "setConditionAgeValueAtIndex";
  index: number;
  value: number;
}

interface SetConditionFlagsValueAtIndexAction {
  type: "setConditionFlagsValueAtIndex";
  index: number;
  value: PatientFlags;
}

interface SetConditionRdvTypeValueAtIndexAction {
  type: "setConditionRdvTypeValueAtIndex";
  index: number;
  value: RdvTypeValues;
}

interface SetConditionHourValueAtIndexAction {
  type: "setConditionHourValueAtIndex";
  index: number;
  value: number;
}

interface ChangeDelayModeAction {
  type: "changeDelayMode";
  mode: "withDelay" | "anyDelay";
}

type Action =
  | ResetAction
  | ChangeMaxPropositionsAction
  | ChangeDaysAction
  | ChangeTypeAction
  | ChangeFirstSelectAction
  | AddConditionAction
  | RemoveConditionAction
  | SetConditionTypeAtIndexAction
  | SetConditionAgeValueAtIndexAction
  | SetConditionFlagsValueAtIndexAction
  | SetConditionRdvTypeValueAtIndexAction
  | SetConditionHourValueAtIndexAction
  | ChangeDelayModeAction;

function getDefaultRule(): PropositionLimitState {
  return {
    type: "calendarDayLimit",
    maxPropositions: 0,
    days: 1,
  };
}

function getDefaultConditionForType(conditionType: string) {
  switch (conditionType) {
    case "minusAge":
      return { type: "minusAge" as const, value: 80 };
    case "plusAge":
      return { type: "plusAge" as const, value: 80 };
    case "flags":
      return { type: "flags" as const, value: "vip" as const };
    case "notReferred":
      return { type: "notReferred" as const };
    case "referred":
      return { type: "referred" as const };
    case "existingPatient":
      return { type: "existingPatient" as const };
    case "newPatient":
      return { type: "newPatient" as const };
    case "rdvType":
      return { type: "rdvType" as const, value: RdvTypeEnums.PCONSULT };
    case "afterHour":
      return { type: "afterHour" as const, value: 1080 };
    case "beforeHour":
      return { type: "beforeHour" as const, value: 120 };
    default:
      return { type: "newPatient" as const };
  }
}

function rootReducer(
  state: PropositionLimitState,
  action: Action
): PropositionLimitState {
  switch (action.type) {
    case "reset":
      return getDefaultRule();

    case "changeMaxPropositions":
      if (state.type === "shortcutLimit") return state;

      return {
        ...state,
        maxPropositions: action.value,
      };

    case "changeDays":
      if (state.type === "shortcutLimit") return state;

      if (state.type === "calendarDayLimit") {
        return {
          ...state,
          days: action.value,
        };
      } else if (state.type === "opendayLimit") {
        return {
          ...state,
          openDays: action.value,
        };
      } else if (state.type === "calendarWeekLimit") {
        return {
          ...state,
          weeks: action.value,
        };
      }
      return state;

    case "changeType":
      if (state.type === "shortcutLimit") return state;

      const currentMaxPropositions = state.maxPropositions;
      const currentValue =
        state.type === "calendarDayLimit"
          ? state.days
          : state.type === "opendayLimit"
            ? state.openDays
            : state.weeks;

      if (action.limitType === "calendarDayLimit") {
        return {
          type: "calendarDayLimit",
          maxPropositions: currentMaxPropositions,
          days: currentValue,
        };
      } else if (action.limitType === "opendayLimit") {
        return {
          type: "opendayLimit",
          maxPropositions: currentMaxPropositions,
          openDays: currentValue,
        };
      } else if (action.limitType === "calendarWeekLimit") {
        return {
          type: "calendarWeekLimit",
          maxPropositions:
            currentMaxPropositions === 0 ? 1 : currentMaxPropositions,
          weeks: currentValue,
        };
      }
      return state;

    case "changeFirstSelect":
      switch (action.value) {
        case "shortcut":
          return {
            type: "shortcutLimit",
            conditionsAND: [{ type: "newPatient" }],
          };

        case "none":
          if (state.type === "calendarDayLimit") {
            return { ...state, maxPropositions: 0 };
          } else if (state.type === "opendayLimit") {
            return { ...state, maxPropositions: 0 };
          } else if (state.type === "shortcutLimit") {
            return { type: "calendarDayLimit", maxPropositions: 0, days: 1 };
          } else {
            return { ...state, maxPropositions: 0 };
          }

        case "limit":
        default:
          if (state.type === "shortcutLimit") {
            return { type: "calendarDayLimit", maxPropositions: 1, days: 1 };
          } else if (state.maxPropositions === 0) {
            return { ...state, maxPropositions: 1 };
          }
          return state;
      }
    case "addCondition":
      if (state.type === "shortcutLimit") {
        return {
          ...state,
          conditionsAND: [
            ...(state.conditionsAND || []),
            { type: "newPatient" },
          ],
        };
      }
      return state;
    case "removeCondition":
      if (state.type === "shortcutLimit") {
        return {
          ...state,
          conditionsAND: (state.conditionsAND || []).filter(
            (_, index) => index !== action.index
          ),
        };
      }
      return state;
    case "setConditionTypeAtIndex":
      if (state.type === "shortcutLimit") {
        return {
          ...state,
          conditionsAND: (state.conditionsAND || []).map((condition, index) =>
            index === action.index
              ? getDefaultConditionForType(action.conditionType)
              : condition
          ),
        } as PropositionLimitState;
      }
      return state;
    case "setConditionAgeValueAtIndex":
      if (state.type === "shortcutLimit") {
        return {
          ...state,
          conditionsAND: (state.conditionsAND || []).map((condition, index) =>
            index === action.index &&
            (condition.type === "minusAge" || condition.type === "plusAge")
              ? { ...condition, value: action.value }
              : condition
          ),
        };
      }
      return state;
    case "setConditionFlagsValueAtIndex":
      if (state.type === "shortcutLimit") {
        return {
          ...state,
          conditionsAND: (state.conditionsAND || []).map((condition, index) =>
            index === action.index && condition.type === "flags"
              ? { ...condition, value: action.value }
              : condition
          ),
        };
      }
      return state;
    case "setConditionRdvTypeValueAtIndex":
      if (state.type === "shortcutLimit") {
        return {
          ...state,
          conditionsAND: (state.conditionsAND || []).map((condition, index) =>
            index === action.index && condition.type === "rdvType"
              ? { ...condition, value: action.value }
              : condition
          ),
        };
      }
      return state;
    case "setConditionHourValueAtIndex":
      if (state.type === "shortcutLimit") {
        return {
          ...state,
          conditionsAND: (state.conditionsAND || []).map((condition, index) =>
            index === action.index &&
            (condition.type === "afterHour" || condition.type === "beforeHour")
              ? { ...condition, value: action.value }
              : condition
          ),
        };
      }
      return state;
    case "changeDelayMode":
      if (state.type === "shortcutLimit") return state;

      if (action.mode === "anyDelay") {
        // Convertir vers un état "quel que soit le délai" - on garde juste maxPropositions
        return {
          type: "calendarDayLimit",
          maxPropositions: state.maxPropositions,
          days: -1, // Valeur spéciale pour indiquer "quel que soit le délai"
        };
      } else {
        // Convertir vers un état normal avec délai
        if (state.type === "calendarDayLimit" && state.days === -1) {
          return {
            ...state,
            days: 1,
          };
        } else if (state.type === "opendayLimit" && state.openDays === -1) {
          return {
            ...state,
            openDays: 1,
          };
        } else if (state.type === "calendarWeekLimit" && state.weeks === -1) {
          return {
            ...state,
            weeks: 1,
          };
        }
      }
      return state;
  }
}

interface PropositionLimitLineProps {
  state: PropositionLimitState;
  dispatch: (action: Action) => void;
}

const PropositionLimitLine = (props: PropositionLimitLineProps) => {
  const state = props.state;
  const dispatch = props.dispatch;

  const handleMaxPropositionsChange = (value: number) => {
    dispatch({ type: "changeMaxPropositions", value });
  };

  const handleDaysChange = (value: number) => {
    dispatch({ type: "changeDays", value });
  };

  const handleTypeChange = (
    limitType: "calendarDayLimit" | "opendayLimit" | "calendarWeekLimit"
  ) => {
    dispatch({ type: "changeType", limitType });
  };

  const weekOptionsKeyMap = {
    "1": "d'un rdv",
    "2": "de deux rdvs",
    "3": "de trois rdvs",
    "4": "de quatre rdvs",
    "5": "de cinq rdvs",
    "10": "de dix rdvs",
    "15": "de quinze rdvs",
    "20": "de vingt rdvs",
    "25": "de vingt-cinq rdvs",
    "30": "de trente rdvs",
  };

  const horaires = trpc.horaire.byDate.useQuery({
    date: new Date(2021, 0, 1),
  }).data;
  const horairesExtremes = (horaires &&
    Transforms.horaires.horairesExtremesForWeek(horaires)) || {
    start: { hour: 0, minute: 0 },
    end: { hour: 23, minute: 59 },
  };
  const prefs = trpc.preference.get.useQuery().data;
  const hmPossibles =
    (prefs?.duree_min_rdv &&
      Transforms.horaires.getAllHMinForPlage(
        horairesExtremes,
        prefs?.duree_min_rdv
      )) ||
    [];

  const FlagSelect = (props: {
    value: PatientFlags;
    onChange: (value: PatientFlags) => void;
  }) => {
    return (
      <Select2
        selected={props.value}
        onSelect={props.onChange}
        defaultKey="vip"
        keyMap={CadendarConsts.patientFlags
          .map((flag) => ({
            [flag]: flag,
          }))
          .reduce((acc, curr) => ({ ...acc, ...curr }), {})}
        className=""
      />
    );
  };

  const RdvTypeSelect = (props: {
    value: RdvTypeValues;
    onChange: (value: RdvTypeValues) => void;
  }) => {
    const handleChange = (key: typeof RdvTypeEnums.keyType) => {
      props.onChange(RdvTypeEnums[key]);
    };
    return (
      <Select4
        selected={RdvTypeEnums.key(props.value)}
        onSelect={handleChange}
        defaultKey={"PCONSULT"}
        className=""
        items={RdvTypeEnums.items}
      />
    );
  };

  const PatientConditionChooser = (props: {
    value:
      | "minusAge"
      | "plusAge"
      | "flags"
      | "newPatient"
      | "existingPatient"
      | "notReferred"
      | "referred"
      | "rdvType"
      | "afterHour"
      | "beforeHour";
    onChange: (
      value:
        | "minusAge"
        | "plusAge"
        | "flags"
        | "newPatient"
        | "existingPatient"
        | "notReferred"
        | "referred"
        | "rdvType"
        | "afterHour"
        | "beforeHour"
    ) => void;
  }) => {
    return (
      <Select2
        selected={props.value}
        onSelect={props.onChange}
        defaultKey="flags"
        keyMap={{
          minusAge: "patients âgés de moins de",
          plusAge: "patients âgés de plus de",
          flags: "patients avec le drapeau",
          newPatient: "patients nouveaux",
          existingPatient: "patients anciens",
          notReferred: "patients non adressés",
          referred: "patients adressés",
          rdvType: "rdv de type",
          afterHour: "rdv après",
          beforeHour: "rdv avant",
        }}
        className=""
      />
    );
  };

  const FirstSelect = (props: {
    value: "limit" | "none" | "shortcut";
    onChange: (value: "limit" | "none" | "shortcut" | undefined) => void;
  }) => {
    return (
      <Select2
        selected={props.value}
        defaultString="Choisir"
        onSelect={props.onChange}
        keyMap={{
          limit: "Pas plus",
          none: "Aucun",
          shortcut: "Les règles suivantes ne s'appliquent pas aux",
        }}
        className=""
      />
    );
  };

  const DelayModeSelect = (props: {
    value: "withDelay" | "anyDelay";
    onChange: (value: "withDelay" | "anyDelay") => void;
  }) => {
    return (
      <Select2
        selected={props.value}
        onSelect={props.onChange}
        defaultKey="withDelay"
        keyMap={{
          withDelay: "quand on est à moins de",
          anyDelay: "quel que soit le délai",
        }}
        className=""
      />
    );
  };

  const firstValue: "limit" | "none" | "shortcut" =
    state.type === "shortcutLimit"
      ? "shortcut"
      : (state.type === "calendarDayLimit" || state.type === "opendayLimit") &&
          state.maxPropositions === 0
        ? "none"
        : "limit";

  const delayMode: "withDelay" | "anyDelay" =
    state.type !== "shortcutLimit" &&
    ((state.type === "calendarDayLimit" && state.days === -1) ||
      (state.type === "opendayLimit" && state.openDays === -1) ||
      (state.type === "calendarWeekLimit" && state.weeks === -1))
      ? "anyDelay"
      : "withDelay";

  const handleFirstChange = (
    value: "limit" | "none" | "shortcut" | undefined
  ) => {
    if (!value) return;
    dispatch({ type: "changeFirstSelect", value });
  };

  const handleDelayModeChange = (mode: "withDelay" | "anyDelay") => {
    dispatch({ type: "changeDelayMode", mode });
  };

  return (
    <div className="p-2 flex flex-wrap gap-3 items-center">
      <div className="min-w-80">
        <FirstSelect value={firstValue} onChange={handleFirstChange} />
      </div>
      {state.type !== "shortcutLimit" && firstValue === "limit" && (
        <div className="min-w-32">
          {state.type === "calendarWeekLimit" ? (
            <Select2
              selected={
                state.maxPropositions.toString() as keyof typeof weekOptionsKeyMap
              }
              onSelect={(value: keyof typeof weekOptionsKeyMap) => {
                handleMaxPropositionsChange(parseInt(value));
              }}
              defaultKey="1"
              keyMap={weekOptionsKeyMap}
              className=""
            />
          ) : (
            <Select2
              selected={
                state.maxPropositions >= 1 && state.maxPropositions <= 10
                  ? (state.maxPropositions.toString() as
                      | "1"
                      | "2"
                      | "3"
                      | "4"
                      | "5"
                      | "6"
                      | "7"
                      | "8"
                      | "9"
                      | "10")
                  : "1"
              }
              onSelect={(
                value:
                  | "1"
                  | "2"
                  | "3"
                  | "4"
                  | "5"
                  | "6"
                  | "7"
                  | "8"
                  | "9"
                  | "10"
              ) => {
                handleMaxPropositionsChange(parseInt(value));
              }}
              defaultKey="1"
              keyMap={{
                "1": "d'un rdv",
                "2": "de deux rdvs",
                "3": "de trois rdvs",
                "4": "de quatre rdvs",
                "5": "de cinq rdvs",
                "6": "de six rdvs",
                "7": "de sept rdvs",
                "8": "de huit rdvs",
                "9": "de neuf rdvs",
                "10": "de dix rdvs",
              }}
              className=""
            />
          )}
        </div>
      )}
      {(firstValue === "limit" || firstValue === "none") && (
        <div className="min-w-48">
          <DelayModeSelect value={delayMode} onChange={handleDelayModeChange} />
        </div>
      )}
      {(firstValue === "limit" || firstValue === "none") &&
        state.type !== "shortcutLimit" &&
        delayMode === "withDelay" && (
          <div className="w-20">
            <IntegerInput
              value={
                state.type === "calendarDayLimit"
                  ? state.days
                  : state.type === "opendayLimit"
                    ? state.openDays
                    : state.weeks
              }
              showValidation={false}
              onValueChange={(value) => {
                if (value !== null && value > 0) {
                  handleDaysChange(value);
                }
              }}
            />
          </div>
        )}
      {(firstValue === "limit" || firstValue === "none") &&
        state.type !== "shortcutLimit" &&
        delayMode === "withDelay" && (
          <div className="min-w-52">
            <Select2
              selected={state.type}
              onSelect={handleTypeChange}
              defaultKey="calendarDayLimit"
              keyMap={{
                calendarDayLimit: (() => {
                  const days =
                    state.type === "calendarDayLimit"
                      ? Math.max(1, state.days)
                      : 1;
                  return days === 1 ? "jour calendaire" : "jours calendaires";
                })(),
                opendayLimit: (() => {
                  const openDays =
                    state.type === "opendayLimit"
                      ? Math.max(1, state.openDays)
                      : 1;
                  return openDays === 1
                    ? "jour d'ouverture du cabinet"
                    : "jours d'ouverture du cabinet";
                })(),
                calendarWeekLimit: (() => {
                  const weeks =
                    state.type === "calendarWeekLimit"
                      ? Math.max(1, state.weeks)
                      : 1;
                  return weeks === 1
                    ? "semaine calendaire"
                    : "semaines calendaires";
                })(),
              }}
              className=""
            />
          </div>
        )}
      {(firstValue === "limit" || firstValue === "none") &&
        delayMode === "withDelay" && (
          <div className="min-w-32">
            <span>de la date du rdv</span>
          </div>
        )}

      {state.type === "shortcutLimit" && (
        <div className="flex gap-2">
          {(state.type === "shortcutLimit"
            ? state.conditionsAND || []
            : []
          ).map((condition, index: number) => (
            <div key={index} className="flex gap-2 items-center">
              {index > 0 ? <span className="font-bold">et</span> : null}
              <PatientConditionChooser
                value={condition.type}
                onChange={(value) =>
                  dispatch({
                    type: "setConditionTypeAtIndex",
                    index,
                    conditionType: value,
                  })
                }
              />
              {condition.type === "minusAge" || condition.type === "plusAge" ? (
                <div className="flex gap-2 items-center w-20">
                  <IntegerInput
                    value={condition.value}
                    showValidation={false}
                    onValueChange={(value) => {
                      if (value) {
                        dispatch({
                          type: "setConditionAgeValueAtIndex",
                          index,
                          value: value,
                        });
                      }
                    }}
                  />{" "}
                  ans
                </div>
              ) : null}
              {condition.type === "flags" ? (
                <FlagSelect
                  value={condition.value}
                  onChange={(value) => {
                    dispatch({
                      type: "setConditionFlagsValueAtIndex",
                      index,
                      value,
                    });
                  }}
                />
              ) : null}
              {condition.type === "rdvType" ? (
                <RdvTypeSelect
                  value={condition.value}
                  onChange={(value: RdvTypeValues) => {
                    dispatch({
                      type: "setConditionRdvTypeValueAtIndex",
                      index,
                      value,
                    });
                  }}
                />
              ) : null}
              {condition.type === "afterHour" ||
              condition.type === "beforeHour" ? (
                <HMInput
                  hmSelected={
                    formaters.getHourMinutesFromMinutes(condition.value) || {
                      hour: 12,
                      minute: 0,
                    }
                  }
                  handleHMChange={(hm: { hour: number; minute: number }) =>
                    dispatch({
                      type: "setConditionHourValueAtIndex",
                      value: formaters.getHourAsMinutesFromHMObject(hm),
                      index,
                    })
                  }
                  hmPossibles={hmPossibles}
                />
              ) : null}
            </div>
          ))}
          {state.type === "shortcutLimit" && (
            <div className="flex items-center">
              <IconButton
                tooltip="Supprimer dernière condition"
                onClick={() =>
                  dispatch({
                    type: "removeCondition",
                    index:
                      (state.type === "shortcutLimit" ||
                      "conditionsAND" in state
                        ? state.conditionsAND || []
                        : []
                      ).length - 1,
                  })
                }
              >
                <FaMinus />
              </IconButton>
            </div>
          )}
          {state.type === "shortcutLimit" && (
            <div className="flex items-center">
              <IconButton
                tooltip="Ajouter une condition"
                onClick={() =>
                  dispatch({
                    type: "addCondition",
                  })
                }
              >
                <FaPlus />
              </IconButton>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const PropositionLimitReducer = (
  state: PropositionLimitState[] = [],
  action:
    | { ruleIndex: number; action: Action; type: "lineAction" }
    | { type: "addLine" }
    | { type: "removeLine"; lineNumber: number }
    | { type: "moveLineUp"; lineNumber: number }
    | { type: "moveLineDown"; lineNumber: number }
    | { type: "assignState"; state: PropositionLimitState[] }
) => {
  if (action.type === "addLine") {
    return [...state, getDefaultRule()];
  }
  if (action.type === "removeLine") {
    return [
      ...state.slice(0, action.lineNumber),
      ...state.slice(action.lineNumber + 1),
    ];
  }
  if (action.type === "moveLineUp") {
    if (action.lineNumber === 0) return state; // Déjà en haut
    const newState = [...state];
    [newState[action.lineNumber - 1], newState[action.lineNumber]] = [
      newState[action.lineNumber],
      newState[action.lineNumber - 1],
    ];
    return newState;
  }
  if (action.type === "moveLineDown") {
    if (action.lineNumber === state.length - 1) return state; // Déjà en bas
    const newState = [...state];
    [newState[action.lineNumber], newState[action.lineNumber + 1]] = [
      newState[action.lineNumber + 1],
      newState[action.lineNumber],
    ];
    return newState;
  }
  if (action.type === "lineAction") {
    return state.map((r, idx) =>
      idx === action.ruleIndex ? rootReducer(r, action.action) : r
    );
  }
  if (action.type === "assignState") {
    return action.state;
  }
  return state;
};

interface PropositionLimitsCompProps {
  limits: PropositionLimit[] | null;
}

export const PropositionLimitsComp = (props: PropositionLimitsCompProps) => {
  const [state, dispatch] = useReducer(PropositionLimitReducer, []);

  console.log("PropositionLimitsComp state", state);

  useEffect(() => {
    if (props.limits) {
      // Convertir les valeurs élevées (999) en mode "anyDelay" (-1)
      const convertedLimits = props.limits.map((limit) => {
        if (limit.type === "calendarDayLimit" && limit.days >= 999) {
          return { ...limit, days: -1 };
        }
        if (limit.type === "opendayLimit" && limit.openDays >= 999) {
          return { ...limit, openDays: -1 };
        }
        if (limit.type === "calendarWeekLimit" && limit.weeks >= 999) {
          return { ...limit, weeks: -1 };
        }
        return limit;
      });
      dispatch({ type: "assignState", state: convertedLimits });
    }
  }, [props.limits]);

  const hasChanges = JSON.stringify(state) !== JSON.stringify(props.limits);
  const isValid = state.every((limit) => {
    if (limit.type === "shortcutLimit") {
      return true; // Les shortcutLimit ont leur propre validation
    }
    // Si c'est un délai "quel que soit" (valeur -1), on le convertit temporairement pour la validation
    if (limit.type === "calendarDayLimit" && limit.days === -1) {
      return PropositionLimitSchema.safeParse({ ...limit, days: 1 }).success;
    }
    if (limit.type === "opendayLimit" && limit.openDays === -1) {
      return PropositionLimitSchema.safeParse({ ...limit, openDays: 1 })
        .success;
    }
    if (limit.type === "calendarWeekLimit" && limit.weeks === -1) {
      return PropositionLimitSchema.safeParse({ ...limit, weeks: 1 }).success;
    }
    return PropositionLimitSchema.safeParse(limit).success;
  });

  if (!isValid) {
    console.log("Invalid limits state", state);
    console.log(
      "Invalid limits errors",
      state.map((limit) => PropositionLimitSchema.safeParse(limit).error)
    );
  }

  const handleDispatch = (ruleIndex: number) => (action: Action) =>
    dispatch({ ruleIndex, action, type: "lineAction" });

  const utils = trpc.useUtils();
  const submitLimitsMutation =
    trpc.rule.submitPropositionLimitsForOwner.useMutation({
      onSuccess: () => {
        utils.rule.getAll.invalidate();
      },
    });

  const handleSubmit = () => {
    const parsed = state.map((limit) => {
      // Convertir les valeurs -1 (quel que soit le délai) en valeurs appropriées pour le serveur
      if (limit.type === "shortcutLimit") {
        return PropositionLimitSchema.parse(limit);
      }

      if (limit.type === "calendarDayLimit" && limit.days === -1) {
        // Pour "quel que soit le délai", on utilise une valeur très élevée
        return PropositionLimitSchema.parse({ ...limit, days: 999 });
      }
      if (limit.type === "opendayLimit" && limit.openDays === -1) {
        return PropositionLimitSchema.parse({ ...limit, openDays: 999 });
      }
      if (limit.type === "calendarWeekLimit" && limit.weeks === -1) {
        return PropositionLimitSchema.parse({ ...limit, weeks: 999 });
      }

      return PropositionLimitSchema.parse(limit);
    });
    submitLimitsMutation.mutate(parsed);
  };

  return (
    <div className="m-4">
      <div className="text-xl">Limites de propositions</div>
      <div className="m-1">
        {state.map((limit, idx) => (
          <div key={idx} className="flex items-center">
            <div className="flex gap-1">
              {idx > 0 ? (
                <IconButton
                  onClick={() =>
                    dispatch({ type: "moveLineUp", lineNumber: idx })
                  }
                  tooltip="Déplacer vers le haut"
                >
                  <FaArrowUp />
                </IconButton>
              ) : (
                <div className="w-8"></div>
              )}
              {idx < state.length - 1 ? (
                <IconButton
                  onClick={() =>
                    dispatch({ type: "moveLineDown", lineNumber: idx })
                  }
                  tooltip="Déplacer vers le bas"
                >
                  <FaArrowDown />
                </IconButton>
              ) : (
                <div className="w-8"></div>
              )}
            </div>
            <IconButton
              onClick={() => dispatch({ type: "removeLine", lineNumber: idx })}
              tooltip="Retirer règle"
            >
              <FaMinus />
            </IconButton>
            <PropositionLimitLine
              state={limit}
              dispatch={handleDispatch(idx)}
            />
          </div>
        ))}
        <div className="pt-2">
          <IconButton
            onClick={() => dispatch({ type: "addLine" })}
            tooltip="Ajouter une règle"
          >
            <FaPlus />
          </IconButton>
        </div>
        <div className="pt-3">
          <button
            className="bg-blue-500 text-white px-4 py-2 rounded disabled:opacity-50"
            onClick={handleSubmit}
            disabled={!hasChanges || !isValid || submitLimitsMutation.isPending}
          >
            {submitLimitsMutation.isPending
              ? "Enregistrement..."
              : "Enregistrer"}
          </button>
          {submitLimitsMutation.error && (
            <div className="text-red-500 text-sm mt-2">
              Erreur lors de l'enregistrement:{" "}
              {submitLimitsMutation.error.message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
